import { useState } from 'react';

import useTheme from 'hooks/useTheme';
import ArrowNext from 'styles/assets/icons/ArrowNext';
import ArrowPrevious from 'styles/assets/icons/ArrowPrevious';
import CrossIcon from 'styles/assets/icons/CloseIcon';
import DoubleTriangleArrow from 'styles/assets/icons/DoubleTriangleArrow';
import TickRound from 'styles/assets/icons/TickRound';
import Box from 'UIKit/Box';
import MaterialConfirmDialog from 'UIKit/MaterialConfirmDialog';

import {
  TrackerIntroSlideFirstDark,
  TrackerIntroSlideFirstLight,
  TrackerIntroSlideSecondDark,
  TrackerIntroSlideSecondLight,
  TrackerIntroSlideThirdDark,
  TrackerIntroSlideThirdLight,
} from './slidesImageSvgs';
import styles from './trackerIntroSlides.module.scss';

const TrackerIntroSlides = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [slideCount, setSlideCount] = useState(1);
  const { isLightTheme } = useTheme();

  const handlePrevious = () => {
    setSlideCount(slideCount - 1);
  };

  const handleNext = () => {
    setSlideCount(slideCount + 1);
    if (slideCount === 3) {
      onClose();
      setSlideCount(1);
    }
  };

  const slideContent = () => {
    switch (slideCount) {
      case 1:
        return {
          title: 'A smarter way to trade crypto',
          bulletPoint1: 'Trackers mirror prices of crypto assets',
          bulletPoint2: 'Beginner friendly instruments - no knowledge of F&O needed',
          bulletPoint3: 'Can be traded directly with INR',
          svgComp: isLightTheme ? (
            <TrackerIntroSlideFirstLight />
          ) : (
            <TrackerIntroSlideFirstDark />
          ),
        };
      case 2:
        return {
          title: 'Why is it a smarter choice?',
          bulletPoint1: 'Tighter spreads and deeper liquidity than any spot exchange',
          bulletPoint2: 'Lower trading fees than any spot exchange',
          bulletPoint3: 'No 1% TDS, No flat 30% tax, loss offset available',
          svgComp: isLightTheme ? (
            <TrackerIntroSlideSecondLight />
          ) : (
            <TrackerIntroSlideSecondDark />
          ),
        };
      case 3:
        return {
          title: 'Any trade-offs for the advantages?',
          bulletPoint1: 'A daily cost is charged on your tracker assets',
          bulletPoint2: 'Tracker assets cannot be withdrawn',
          bulletPoint3: 'No leverage. No shorting. Use F&O for that',
          svgComp: isLightTheme ? (
            <TrackerIntroSlideThirdLight />
          ) : (
            <TrackerIntroSlideThirdDark />
          ),
        };
      default:
        return <Box as="h2">Slide 1</Box>;
    }
  };

  return (
    (<MaterialConfirmDialog
      open={open}
      onClose={onClose}
      showHeader={false}
      showFooter={false}
      containerClassName={styles.introModal}
      bodyClassName={styles.introModalBody}
      rootClassName={styles.introRoot}
      contentClassName={styles.introContent}
    >
      <Box className={styles.trackerIntroSlides}>
        <Box as="p" className={styles.title}>
          {slideContent().title}
        </Box>
        <button
          type="button"
          data-testid="kfYdIc-PsGcMVME6W2P6r"
          className={styles.closeIcon}
          onClick={onClose}
        >
          <CrossIcon color="var(--main-text-secondary)" />
        </button>
        <Box className={styles.image}>{slideContent().svgComp}</Box>
        {slideCount === 3 && (
          <Box className={styles.introPeriodOffer}>
            <Box as="p" className={styles.offerTitle}>
              Introductory Period Offer:
            </Box>
            <Box as="p" className={styles.offerText}>
              <DoubleTriangleArrow /> No Holding Cost
            </Box>
          </Box>
        )}
        <Box className={styles.bulletPoints}>
          <Box className={styles.bulletPoint}>
            <TickRound className={styles.tickIcon} color="var(--positive-bg-primary)" />
            <div>{slideContent().bulletPoint1}</div>
          </Box>
          <Box className={styles.bulletPoint}>
            <TickRound className={styles.tickIcon} color="var(--positive-bg-primary)" />
            <div>{slideContent().bulletPoint2}</div>
          </Box>
          <Box className={styles.bulletPoint}>
            <TickRound className={styles.tickIcon} color="var(--positive-bg-primary)" />
            <div>{slideContent().bulletPoint3}</div>
          </Box>
        </Box>
        <Box className={styles.buttons}>
          {slideCount > 1 && (
            <button
              data-testid="PqFtGF2gYhVh7rjhccSVE"
              onClick={handlePrevious}
              disabled={slideCount === 1}
              className={styles.previousButton}
              type="button"
              data-palette="TrackerIntroSlides">
              <ArrowPrevious /> Previous
            </button>
          )}
          <button
            data-testid="LpKGf7W0J9L4fXVLUXBDX"
            onClick={handleNext}
            className={styles.nextButton}
            type="button"
          >
            {slideCount === 3 ? 'Finish' : 'Next'} <ArrowNext />
          </button>
        </Box>
      </Box>
    </MaterialConfirmDialog>)
  );
};

export default TrackerIntroSlides;
